import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ADMIN_PATH } from "@/App";

const LOGO_URL = "/logo.png";

export default function AdminLoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.token) localStorage.setItem("admin_token", data.token);
            localStorage.setItem("admin_user", JSON.stringify({ email: data.email, role: data.role }));
            toast.success("Zalogowano pomyślnie.");
            navigate(`${ADMIN_PATH}/dashboard`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Logowanie nie powiodło się.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center bg-[#FAF8F3] px-6 py-12 bg-grain">
            <div className="w-full max-w-md">
                <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0D4F3C] mb-6 transition-colors">
                    <ArrowLeft size={16} /> Powrót na stronę główną
                </Link>

                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10">
                    <div className="flex flex-col items-center mb-8">
                        <img src={LOGO_URL} alt="Dąbrówka Wiślana" className="w-20 h-20 rounded-full ring-2 ring-[#D4A537]/40 mb-4" />
                        <h1 className="font-serif text-3xl text-[#092E22] mb-1">Panel administratora</h1>
                        <p className="text-sm text-gray-500">Dąbrówka Wiślana – Aluzyjna 25 C/D/E/F</p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label className="text-[#092E22] font-medium flex items-center gap-2">
                                <Mail size={14} /> E-mail
                            </Label>
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@aluzyjna25cdef.pl"
                                data-testid="admin-email-input"
                                className="h-12 rounded-xl px-4 bg-[#FAF8F3] border-gray-200 focus-visible:ring-[#0D4F3C]"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[#092E22] font-medium flex items-center gap-2">
                                <Lock size={14} /> Hasło
                            </Label>
                            <Input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="admin-password-input"
                                className="h-12 rounded-xl px-4 bg-[#FAF8F3] border-gray-200 focus-visible:ring-[#0D4F3C]"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-button"
                            className="w-full h-12 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white font-semibold transition-all hover:-translate-y-0.5"
                        >
                            {loading ? "Logowanie..." : "Zaloguj się"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
